import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Receipt, ShoppingBag, FileText, User, Plus, Trash2, ArrowRight, Search, X, Printer, CheckCircle2, Clock, AlertCircle, Ban, ZoomIn, ZoomOut as ZoomOutIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import portalApi, { getPortalClient, clearPortalSession, savePortalSession } from '../../utils/portalApi';
import api from '../../utils/api';

const TABS = [
  { id: 'overview', label: 'Estado de cuenta', icon: User },
  { id: 'invoices', label: 'Mis facturas', icon: Receipt },
  { id: 'preorders', label: 'Mis pre-pedidos', icon: FileText },
  { id: 'new', label: 'Nuevo pre-pedido', icon: ShoppingBag },
];

const formatCurrency = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('es-CO', { maximumFractionDigits: 0 });
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const PortalHome = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(getPortalClient());
  const [activeTab, setActiveTab] = useState('overview');
  const [statement, setStatement] = useState(null);
  const [preOrders, setPreOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('portal_token')) {
      navigate('/portal/login');
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [meRes, stmtRes, preRes] = await Promise.all([
        portalApi.get('/portal/me'),
        portalApi.get('/portal/statement'),
        portalApi.get('/portal/pre-orders'),
      ]);
      // Refrescar cache de cliente
      const updated = {
        id: meRes.data.id,
        name: meRes.data.name,
        code: meRes.data.access_code,
        phone: meRes.data.phone,
        email: meRes.data.email,
        balance: meRes.data.balance,
      };
      setClient(updated);
      savePortalSession(localStorage.getItem('portal_token'), updated);
      setStatement(stmtRes.data);
      setPreOrders(preRes.data);
    } catch (error) {
      console.error('Error loading portal', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearPortalSession();
    navigate('/');
  };

  const reloadPreOrders = async () => {
    try {
      const res = await portalApi.get('/portal/pre-orders');
      setPreOrders(res.data);
    } catch {}
  };

  const handleCancelPreOrder = async (id) => {
    if (!window.confirm('¿Cancelar este pre-pedido?')) return;
    try {
      await portalApi.delete(`/portal/pre-orders/${id}`);
      toast.success('Pre-pedido cancelado');
      reloadPreOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  if (!client) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="px-4 md:px-12 lg:px-16 py-5 flex items-center justify-between">
          <Link to="/" className="font-['Playfair_Display'] text-2xl tracking-tighter font-bold">HAUTE</Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Cliente</span>
              <span className="text-sm">{client.name}</span>
            </div>
            <span data-testid="portal-client-code" className="px-3 py-1.5 text-xs font-mono tracking-widest bg-white/10 border border-white/20 rounded">
              {client.code}
            </span>
            <button
              onClick={handleLogout}
              data-testid="portal-logout"
              className="p-2.5 border border-white/10 hover:border-white/40 rounded-sm transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="px-4 md:px-12 lg:px-16 flex gap-1 overflow-x-auto border-t border-white/5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`portal-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3.5 text-xs uppercase tracking-[0.15em] whitespace-nowrap border-b-2 transition-all ${
                  isActive
                    ? 'border-white text-white'
                    : 'border-transparent text-white/50 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Main */}
      <main className="px-4 md:px-12 lg:px-16 py-8 md:py-12 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-20 text-white/40 text-sm">Cargando...</div>
        ) : (
          <>
            {activeTab === 'overview' && <OverviewTab statement={statement} client={client} />}
            {activeTab === 'invoices' && <InvoicesTab invoices={statement?.invoices || []} />}
            {activeTab === 'preorders' && (
              <PreOrdersTab preOrders={preOrders} onCancel={handleCancelPreOrder} onCreateNew={() => setActiveTab('new')} />
            )}
            {activeTab === 'new' && <NewPreOrderTab onCreated={() => { reloadPreOrders(); setActiveTab('preorders'); }} />}
          </>
        )}
      </main>
    </div>
  );
};

/* ---------- Tabs ---------- */

const OverviewTab = ({ statement, client }) => {
  if (!statement) return null;
  const balance = client.balance || 0;
  const owes = balance < 0;
  const credit = balance > 0;

  // Últimos 5 pagos
  const recentPayments = (statement.payments || []).slice(0, 5);

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl mb-1">Hola, {client.name}</h1>
        <p className="text-sm text-white/50">Estado de tu cuenta</p>
      </div>

      {/* Saldo adeudado */}
      <div
        data-testid="balance-card"
        className={`p-8 md:p-10 border ${owes ? 'bg-red-500/5 border-red-500/30' : credit ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#0A0A0A] border-white/10'}`}
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
          {owes ? 'Saldo adeudado' : credit ? 'Saldo a favor' : 'Saldo'}
        </p>
        <p className={`font-['Playfair_Display'] text-5xl md:text-6xl font-bold tabular-nums ${owes ? 'text-red-400' : credit ? 'text-emerald-400' : 'text-white'}`}>
          ${formatCurrency(Math.abs(balance))}
        </p>
        {!owes && !credit && (
          <p className="text-sm text-white/40 mt-3">No tienes saldo pendiente.</p>
        )}
      </div>

      {/* Últimos 5 abonos */}
      <div className="bg-[#0A0A0A] border border-white/10">
        <div className="px-5 md:px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-[0.25em] text-white/70 font-semibold">Últimos abonos</h3>
          {recentPayments.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-white/40">
              {recentPayments.length} más reciente{recentPayments.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {recentPayments.length === 0 ? (
          <p className="px-5 md:px-6 py-8 text-center text-sm text-white/40">Aún no hay abonos registrados.</p>
        ) : (
          <ul data-testid="recent-payments" className="divide-y divide-white/5">
            {recentPayments.map((p, idx) => (
              <li key={p.id || idx} className="px-5 md:px-6 py-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white">{formatDate(p.created_at)}</p>
                    {p.payment_method && (
                      <p className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{p.payment_method}</p>
                    )}
                  </div>
                </div>
                <p className="text-base md:text-lg font-semibold text-emerald-400 tabular-nums">
                  +${formatCurrency(p.amount)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const STATUS_THEME = {
  pagada: { label: 'Pagada', dot: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', icon: CheckCircle2 },
  parcial: { label: 'Abono parcial', dot: 'bg-orange-500', text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/40', icon: Clock },
  pendiente: { label: 'Pendiente de pago', dot: 'bg-red-500', text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/40', icon: AlertCircle },
  anulada: { label: 'Anulada', dot: 'bg-white/30', text: 'text-white/40', bg: 'bg-white/5', border: 'border-white/10', icon: Ban },
};
const getStatusTheme = (status) => STATUS_THEME[status] || STATUS_THEME.pendiente;

const InvoicesTab = ({ invoices }) => {
  const [selected, setSelected] = useState(null);

  if (!invoices || invoices.length === 0) {
    return <EmptyState title="Sin facturas" message="Aún no tienes facturas registradas en tu cuenta." />;
  }

  return (
    <div className="space-y-3">
      <h2 className="font-['Playfair_Display'] text-3xl mb-6">Mis facturas</h2>
      <p className="text-sm text-white/50 mb-4">Haz clic en una factura para ver el detalle completo.</p>
      {invoices.map((inv) => {
        const theme = getStatusTheme(inv.status);
        const Icon = theme.icon;
        return (
          <button
            key={inv.id}
            type="button"
            onClick={() => setSelected(inv)}
            data-testid={`invoice-row-${inv.id}`}
            className="w-full text-left bg-[#0A0A0A] border border-white/10 p-4 md:p-5 hover:border-white/30 hover:bg-[#0E0E0E] transition-all duration-300 group"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full ${theme.dot} flex-shrink-0`} />
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Factura</p>
                  <p className="font-semibold">#{inv.invoice_number || inv.id.slice(0, 8)}</p>
                  <p className="text-xs text-white/50 mt-1">{formatDate(inv.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`self-start px-3 py-1.5 text-[10px] uppercase tracking-wider border ${theme.bg} ${theme.text} ${theme.border} font-semibold inline-flex items-center gap-1.5`}>
                  <Icon className="w-3 h-3" />
                  {theme.label}
                </span>
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/5 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40">Total</p>
                <p className="font-semibold">${formatCurrency(inv.total)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40">Pagado</p>
                <p className="text-emerald-400">${formatCurrency(inv.amount_paid)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40">Saldo</p>
                <p className={inv.balance_due > 0 ? 'text-red-400' : 'text-white/50'}>${formatCurrency(inv.balance_due)}</p>
              </div>
            </div>
          </button>
        );
      })}

      {selected && <InvoiceDetailModal invoice={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const InvoiceDetailModal = ({ invoice, onClose }) => {
  const theme = getStatusTheme(invoice.status);
  const items = invoice.items_detail || invoice.items || [];
  const isGrouped = invoice.display_mode === 'category' && invoice.items_grouped && invoice.items_grouped.length > 0;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 print:bg-white print:p-0" onClick={onClose}>
      <div
        data-testid="invoice-detail-modal"
        className="bg-white text-black w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 sm:p-8 print:max-h-none print:max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 border-b pb-4 sm:pb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.2em]">HAUTE</h1>
          <p className="text-gray-500 text-sm">Distribuidor Mayorista de Moda Premium</p>
        </div>

        {/* Meta */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-0 mb-6 sm:mb-8">
          <div className="text-center sm:text-left">
            <h3 className="text-xs uppercase text-gray-400 mb-2">Factura</h3>
            <p className="text-xl sm:text-2xl font-bold">#{invoice.invoice_number || invoice.id.slice(0, 8)}</p>
            <p className="text-sm text-gray-500">{new Date(invoice.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <span className={`inline-block mt-2 px-3 py-1 text-xs uppercase font-semibold ${
              invoice.status === 'pagada' ? 'bg-green-100 text-green-800 border border-green-300' :
              invoice.status === 'parcial' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
              invoice.status === 'pendiente' ? 'bg-red-100 text-red-800 border border-red-300' :
              'bg-gray-100 text-gray-700 border border-gray-300'
            }`}>
              {theme.label}
            </span>
          </div>
          <div className="text-center sm:text-right">
            <h3 className="text-xs uppercase text-gray-400 mb-2">Cliente</h3>
            <p className="font-semibold">{invoice.client_name}</p>
            {invoice.client_document && <p className="text-sm">{invoice.client_document}</p>}
            {invoice.client_phone && <p className="text-sm">{invoice.client_phone}</p>}
          </div>
        </div>

        {/* Items */}
        {isGrouped ? (
          <table className="w-full mb-6 text-sm">
            <thead>
              <tr className="border-b-2 border-black">
                <th className="text-left py-2 text-xs uppercase">Categoría</th>
                <th className="text-center py-2 text-xs uppercase">Cant.</th>
                <th className="text-right py-2 text-xs uppercase">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items_grouped.map((group, idx) => (
                <tr key={idx} className="border-b">
                  <td className="py-3">
                    <span className="font-semibold">{group.category_display}</span>
                    {group.products_detail && (
                      <div className="text-xs text-gray-500 mt-1">
                        {group.products_detail.map((p, i) => (
                          <span key={i}>{p.name} x{p.quantity}{i < group.products_detail.length - 1 ? ', ' : ''}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-center">{group.quantity}</td>
                  <td className="py-3 text-right font-semibold">${group.subtotal.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full mb-6 text-sm">
            <thead>
              <tr className="border-b-2 border-black">
                <th className="text-left py-2 text-xs uppercase">Producto</th>
                <th className="text-center py-2 text-xs uppercase">Cant.</th>
                <th className="text-right py-2 text-xs uppercase">Precio</th>
                <th className="text-right py-2 text-xs uppercase">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="py-3">{item.product_name}{item.size ? ` · ${item.size}` : ''}{item.color ? ` · ${item.color}` : ''}</td>
                  <td className="py-3 text-center">{item.quantity}</td>
                  <td className="py-3 text-right">${item.unit_price?.toLocaleString()}</td>
                  <td className="py-3 text-right font-semibold">${(item.subtotal || item.unit_price * item.quantity).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Totales */}
        <div className="flex justify-end">
          <div className="w-full sm:w-64 space-y-2 p-3 sm:p-0 bg-gray-50 sm:bg-transparent text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${invoice.subtotal?.toLocaleString()}</span>
            </div>
            {invoice.discount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Descuento</span>
                <span>-${invoice.discount.toLocaleString()}</span>
              </div>
            )}
            {invoice.tax > 0 && (
              <div className="flex justify-between">
                <span>Impuesto</span>
                <span>${invoice.tax.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t-2 pt-2">
              <span>TOTAL</span>
              <span>${invoice.total?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>Pagado</span>
              <span>${invoice.amount_paid?.toLocaleString()}</span>
            </div>
            <div className={`flex justify-between font-semibold ${invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
              <span>Saldo</span>
              <span>${invoice.balance_due?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Pagos */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 text-sm">
            <h3 className="text-xs uppercase text-gray-400 mb-3 font-semibold">Historial de pagos</h3>
            {invoice.payments.map((p, idx) => (
              <div key={idx} className="flex justify-between py-1">
                <span>{new Date(p.created_at).toLocaleDateString('es-CO')} · {p.payment_method}</span>
                <span className="font-semibold">${p.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        {invoice.notes && <p className="mt-4 text-sm italic text-gray-500">Notas: {invoice.notes}</p>}

        {/* Acciones (ocultas al imprimir) */}
        <div className="flex gap-3 mt-8 print:hidden">
          <button
            onClick={handlePrint}
            data-testid="invoice-print-btn"
            className="flex-1 bg-black text-white py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-gray-800"
          >
            <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
          </button>
          <button
            onClick={onClose}
            data-testid="invoice-close-btn"
            className="flex-1 border border-gray-300 py-3 text-xs uppercase tracking-wider hover:bg-gray-100"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const PreOrdersTab = ({ preOrders, onCancel, onCreateNew }) => {
  const statusColors = {
    pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    archived: 'bg-white/10 text-white/40 border-white/10',
  };
  const statusLabels = {
    pending: 'Pendiente revisión',
    reviewed: 'Revisado',
    archived: 'Archivado',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-3xl">Mis pre-pedidos</h2>
        <button
          onClick={onCreateNew}
          data-testid="portal-new-preorder-btn"
          className="bg-white text-black hover:bg-gray-200 px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" />
          Nuevo
        </button>
      </div>

      {(!preOrders || preOrders.length === 0) ? (
        <EmptyState title="Sin pre-pedidos" message='Crea tu primer pre-pedido seleccionando los modelos que te interesan. El administrador se contactará contigo para definir cantidades.' />
      ) : (
        <div className="space-y-3">
          {preOrders.map((po) => (
            <div key={po.id} data-testid={`preorder-row-${po.id}`} className="bg-[#0A0A0A] border border-white/10 p-4 md:p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Pre-pedido</p>
                  <p className="text-sm">{formatDate(po.created_at)}</p>
                </div>
                <span className={`px-2.5 py-1 text-[10px] uppercase tracking-wider border ${statusColors[po.status]}`}>
                  {statusLabels[po.status]}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {po.items.map((it, idx) => (
                  <div key={idx} className="bg-[#121212] border border-white/5 p-2 relative">
                    {it.product_image && (
                      <img src={it.product_image} alt={it.product_name} className="w-full h-24 object-cover mb-2" />
                    )}
                    {it.package_quantity > 1 && (
                      <span className="absolute top-1 right-1 bg-emerald-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
                        ×{it.package_quantity}
                      </span>
                    )}
                    <p className="text-xs text-white truncate">{it.product_name}</p>
                    {it.product_brand && <p className="text-[10px] text-white/40 truncate">{it.product_brand}</p>}
                    <p className="text-[10px] text-emerald-400 mt-1">{it.package_quantity || 1} paquete(s)</p>
                  </div>
                ))}
              </div>

              {po.notes && (
                <p className="text-xs text-white/50 mt-3 pt-3 border-t border-white/5 italic">"{po.notes}"</p>
              )}

              {po.status === 'pending' && (
                <button
                  onClick={() => onCancel(po.id)}
                  data-testid={`cancel-preorder-${po.id}`}
                  className="mt-4 inline-flex items-center gap-2 text-xs text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-3 h-3" />
                  Cancelar pre-pedido
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NewPreOrderTab = ({ onCreated }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState({}); // id -> { product, package_quantity }
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, name }
  const [lowStockThreshold, setLowStockThreshold] = useState(30);

  useEffect(() => {
    (async () => {
      try {
        // limit alto para asegurar que TODOS los productos con stock aparecen
        const [prodRes, cfgRes] = await Promise.all([
          api.get('/products', { params: { limit: 1000 } }),
          api.get('/config').catch(() => ({ data: {} })),
        ]);
        setProducts(prodRes.data || []);
        if (typeof cfgRes.data?.low_stock_threshold === 'number') {
          setLowStockThreshold(cfgRes.data.low_stock_threshold);
        }
      } catch {
        toast.error('Error cargando productos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));

  const filtered = products.filter((p) => {
    const matchSearch = !search || (p.name + ' ' + (p.brand || '') + ' ' + (p.sku || '')).toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'all' || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const toggle = (p) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = { product: p, package_quantity: 1 };
      return next;
    });
  };

  const updateQuantity = (productId, delta) => {
    setSelected((prev) => {
      if (!prev[productId]) return prev;
      const current = prev[productId].package_quantity || 1;
      const newQty = Math.max(1, Math.min(99, current + delta));
      return { ...prev, [productId]: { ...prev[productId], package_quantity: newQty } };
    });
  };

  const setQuantity = (productId, value) => {
    setSelected((prev) => {
      if (!prev[productId]) return prev;
      const parsed = parseInt(value, 10);
      const newQty = isNaN(parsed) ? 1 : Math.max(1, Math.min(99, parsed));
      return { ...prev, [productId]: { ...prev[productId], package_quantity: newQty } };
    });
  };

  const selectedList = Object.values(selected);
  const totalPackages = selectedList.reduce((sum, it) => sum + (it.package_quantity || 1), 0);

  const handleSubmit = async () => {
    if (selectedList.length === 0) {
      toast.error('Selecciona al menos un producto');
      return;
    }
    setSaving(true);
    try {
      await portalApi.post('/portal/pre-orders', {
        items: selectedList.map(({ product: p, package_quantity }) => ({
          product_id: p.id,
          product_name: p.name,
          product_image: p.images?.[0] || null,
          product_brand: p.brand,
          product_sku: p.sku,
          package_quantity: package_quantity || 1,
        })),
        notes: notes.trim() || null,
      });
      toast.success('Pre-pedido enviado. El administrador lo revisará.');
      setSelected({});
      setNotes('');
      onCreated && onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el pre-pedido');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Playfair_Display'] text-3xl">Nuevo pre-pedido</h2>
          <p className="text-sm text-white/50 mt-1">Selecciona los modelos que te interesan y la cantidad de paquetes. El administrador te contactará para definir tallas.</p>
        </div>
      </div>

      {/* Nota informativa sobre paquetes */}
      <div data-testid="package-info-note" className="bg-amber-500/5 border border-amber-500/30 p-4 mb-5 flex gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-bold">i</div>
        <div className="text-sm text-white/80 leading-relaxed">
          <strong className="text-amber-400">Sobre los paquetes:</strong> cada paquete es una <strong>curva de tallas</strong> (combinación predefinida). Por favor verifica con el administrador la <strong>cantidad de piezas disponibles por paquete</strong>, ya que puede variar según el modelo (ej. 10, 12 o 15 piezas).
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-5 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar producto, marca o SKU..."
            data-testid="preorder-search"
            className="w-full bg-[#121212] border border-white/10 focus:border-white/30 pl-10 pr-3 py-2.5 text-sm outline-none"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          data-testid="preorder-category"
          className="bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none cursor-pointer"
        >
          <option value="all">Todas las categorías ({products.length})</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Selected pill */}
      {selectedList.length > 0 && (
        <div data-testid="preorder-selected-bar" className="sticky top-4 z-10 bg-emerald-500/10 border border-emerald-500/30 rounded p-3 mb-5 flex items-center justify-between gap-3">
          <span className="text-sm text-emerald-400">
            <strong>{selectedList.length}</strong> modelo(s) · <strong>{totalPackages}</strong> paquete(s)
          </span>
          <button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="preorder-submit"
            className="bg-white text-black hover:bg-gray-200 px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? 'Enviando...' : 'Enviar pre-pedido'}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Products grid */}
      {loading ? (
        <div className="text-center py-20 text-white/40 text-sm">Cargando productos...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {filtered.map((p) => {
            const sel = selected[p.id];
            const isSel = !!sel;
            const stockNum = Number(p.stock || 0);
            const isLowStock = stockNum > 0 && stockNum <= lowStockThreshold;
            return (
              <div
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className={`relative bg-[#0A0A0A] border-2 transition-all duration-200 ${
                  isSel
                    ? 'border-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                    : isLowStock
                      ? 'border-red-500/50 hover:border-red-400/80 shadow-[0_0_24px_-6px_rgba(239,68,68,0.35)]'
                      : 'border-white/10 hover:border-white/30'
                }`}
              >
                <button onClick={() => toggle(p)} className="block w-full text-left relative">
                  <div className="aspect-square bg-[#121212] relative overflow-hidden">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">Sin imagen</div>
                    )}
                    {isSel && (
                      <div className="absolute top-2 right-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold">✓</div>
                    )}
                    {isLowStock && (
                      <div
                        data-testid={`badge-low-stock-${p.id}`}
                        className="absolute bottom-2 left-2 bg-red-500/15 backdrop-blur-sm border border-red-400/60 text-red-300 text-[9px] uppercase tracking-[0.2em] px-2 py-1 font-semibold animate-pulse"
                      >
                        Última curva
                      </div>
                    )}
                  </div>
                  <div className="p-3 pb-2">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 truncate">{p.brand}</p>
                    <p className="text-sm truncate mt-0.5">{p.name}</p>
                  </div>
                </button>

                {/* Botón Ver (lupa) — abre lightbox sin afectar selección */}
                {p.images?.[0] && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setLightbox({ images: p.images, name: p.name, brand: p.brand, startIndex: 0 }); }}
                    data-testid={`preview-${p.id}`}
                    className="absolute top-2 left-2 inline-flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-white/30 transition-colors"
                    aria-label="Ver imagen ampliada"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                    Ver{p.images.length > 1 ? ` (${p.images.length})` : ''}
                  </button>
                )}

                {/* Selector de paquetes (solo si está seleccionado) */}
                {isSel && (
                  <div className="px-3 pb-3 pt-1 border-t border-white/5" onClick={(e) => e.stopPropagation()}>
                    <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Paquetes</label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateQuantity(p.id, -1)}
                        disabled={sel.package_quantity <= 1}
                        data-testid={`qty-minus-${p.id}`}
                        className="w-8 h-8 flex items-center justify-center bg-[#121212] border border-white/10 hover:border-white/30 disabled:opacity-30 text-lg"
                        type="button"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={sel.package_quantity}
                        onChange={(e) => setQuantity(p.id, e.target.value)}
                        data-testid={`qty-input-${p.id}`}
                        className="flex-1 w-full h-8 bg-[#121212] border border-white/10 px-2 text-center text-sm outline-none focus:border-white/30"
                      />
                      <button
                        onClick={() => updateQuantity(p.id, 1)}
                        data-testid={`qty-plus-${p.id}`}
                        className="w-8 h-8 flex items-center justify-center bg-[#121212] border border-white/10 hover:border-white/30 text-lg"
                        type="button"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Notes + submit at bottom */}
      {selectedList.length > 0 && (
        <div className="mt-8 bg-[#0A0A0A] border border-white/10 p-5">
          <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/60">Notas (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            placeholder="Ej: prefiero tonos oscuros, tallas grandes, etc."
            data-testid="preorder-notes"
            className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-3 py-2.5 text-sm outline-none mb-4"
          />
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full bg-white text-black hover:bg-gray-200 py-3 text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-50"
          >
            {saving ? 'Enviando...' : `Enviar pre-pedido (${selectedList.length} modelos · ${totalPackages} paquetes)`}
          </button>
        </div>
      )}

      {lightbox && <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
};

const ImageLightbox = ({ image, onClose }) => {
  const images = image.images && image.images.length > 0 ? image.images : (image.src ? [image.src] : []);
  const [index, setIndex] = useState(image.startIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = React.useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const hasMany = images.length > 1;
  const goPrev = () => { setIndex((i) => (i === 0 ? images.length - 1 : i - 1)); setZoom(1); setPos({ x: 0, y: 0 }); };
  const goNext = () => { setIndex((i) => (i === images.length - 1 ? 0 : i + 1)); setZoom(1); setPos({ x: 0, y: 0 }); };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (hasMany && e.key === 'ArrowLeft') goPrev();
      else if (hasMany && e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMany, images.length, onClose]);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, 4));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.5, 1));
  const reset = () => { setZoom(1); setPos({ x: 0, y: 0 }); };

  const onWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  const onPointerDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPos({
      x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.origY + (e.clientY - dragRef.current.startY),
    });
  };
  const onPointerUp = () => { dragRef.current.dragging = false; };

  const onDoubleClick = () => { if (zoom === 1) setZoom(2); else reset(); };

  // Swipe táctil entre imágenes cuando zoom = 1
  const touchRef = React.useRef({ x: 0, y: 0, time: 0 });
  const onTouchStart = (e) => {
    if (zoom > 1) return;
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (zoom > 1 || !hasMany) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.time;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 500) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[100] flex flex-col"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 pr-3">
          {image.brand && <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">{image.brand}</p>}
          <p className="text-sm text-white truncate">
            {image.name}
            {hasMany && <span className="text-white/40 ml-2">· {index + 1} / {images.length}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= 1}
            data-testid="lightbox-zoom-out"
            className="p-2.5 border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Reducir"
          >
            <ZoomOutIcon className="w-4 h-4" />
          </button>
          <span className="text-xs text-white/60 tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= 4}
            data-testid="lightbox-zoom-in"
            className="p-2.5 border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Ampliar"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            className="ml-2 p-2.5 border border-white/15 hover:border-white/40"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        className="relative flex-1 flex items-center justify-center overflow-hidden touch-pan-y select-none"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onDoubleClick={onDoubleClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ cursor: zoom > 1 ? (dragRef.current.dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        {hasMany && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            data-testid="lightbox-prev"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-black/60 hover:bg-black/85 border border-white/20 hover:border-white/50 backdrop-blur-sm transition-colors"
            aria-label="Imagen anterior"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        )}
        <img
          src={images[index]}
          alt={image.name}
          draggable={false}
          className="max-h-full max-w-full transition-transform duration-200"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`, transformOrigin: 'center center' }}
        />
        {hasMany && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            data-testid="lightbox-next"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-black/60 hover:bg-black/85 border border-white/20 hover:border-white/50 backdrop-blur-sm transition-colors"
            aria-label="Imagen siguiente"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Thumbnails (solo si hay varias) */}
      {hasMany && (
        <div className="px-3 py-3 sm:px-6 border-t border-white/10 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center">
            {images.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setIndex(i); reset(); }}
                data-testid={`lightbox-thumb-${i}`}
                className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 border-2 transition-all ${
                  i === index ? 'border-white' : 'border-white/15 hover:border-white/40 opacity-60 hover:opacity-100'
                }`}
                aria-label={`Imagen ${i + 1}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hint */}
      <div className="px-4 py-2 text-center text-[10px] uppercase tracking-widest text-white/40" onClick={(e) => e.stopPropagation()}>
        {hasMany ? 'Flechas o desliza para cambiar foto · ' : ''}Doble clic para ampliar · Scroll / pellizco para zoom
      </div>
    </div>
  );
};

const EmptyState = ({ title, message }) => (
  <div className="bg-[#0A0A0A] border border-dashed border-white/10 p-12 text-center">
    <p className="text-lg mb-2">{title}</p>
    <p className="text-sm text-white/50 max-w-md mx-auto">{message}</p>
  </div>
);

export default PortalHome;
